<template>
    <div>
        <p class="title is-7">{{'components.common.quick-login-reg.registration.step' | translate}} 1/2</p>

        <!-- Display of registration errors -->
        <div class="message is-danger" v-if="error">
            <div class="message-header">
                <p>{{'components.common.quick-login-reg.registration.error title' | translate}}</p>
                <button class="delete" aria-label="delete" @click="error=null"></button>
            </div>
            <div class="message-body">{{ error }}</div>
        </div>
        <form @submit.prevent="submit()" :class="{ 'is-loading' : loading }" role="form">
            <!-- Messenger type and number input: desktop/mobile-->
            <div class="field has-addons">
                <div class="control has-icons-left is-expanded">
                    <masked-phone v-if="client && client.login.phoneMask"
                                  v-model="phone"
                                  :mask="client.login.phoneMask"
                                  v-validate="{ required: true, length: (client.login.phoneMask.match(/\d/g) || []).length, 'unique-login': true }"
                                  data-vv-name="phone number"
                                  data-vv-validate-on="input"
                                  data-vv-delay="300"
                                  class="input is-small"
                                  autofocus="on" />
                    <input v-else
                           v-model.trim="phone"
                           v-validate="'required|min:9|max:50|unique-login'"
                           data-vv-name="phone number"
                           data-vv-validate-on="input"
                           data-vv-delay="300"
                           :placeholder="'components.common.quick-login-reg.registration.phone number' | translate"
                           class="input is-small"
                           type="text"
                           autofocus="on"
                           maxlength="50" />
                    <span class="icon is-small is-left"><i class="fas fa-phone"></i></span>
                    <p class="help is-danger" v-if="errors.has('phone number:required')">{{"components.common.quick-login-reg.registration.validation.phone number required" | translate}}</p>
                    <p class="help is-danger" v-if="errors.has('phone number:min')">{{ $t("components.common.quick-login-reg.registration.validation.phone number min length", $validator.fields.find({ name: 'phone number' }).rules) }}</p>
                    <p class="help is-danger" v-if="errors.has('phone number:max')">{{ $t("components.common.quick-login-reg.registration.validation.phone number max length", $validator.fields.find({ name: 'phone number' }).rules) }}</p>
                    <p class="help is-danger" v-if="errors.has('phone number:unique-login')">{{"components.common.quick-login-reg.registration.validation.phone number in use" | translate}}</p>
                </div>
                <p class="control is-hidden-mobile">
                    <span class="select is-small">
                        <select v-model="messenger" tabindex="-1">
                            <option v-for="m in availableMessengers" :key="m" :value="m">{{messengerNames[m]}}</option>
                        </select>
                    </span>
                </p>
            </div>
            <!-- Messenger type selection: mobile-->
            <div class="field is-hidden-tablet">
                <div class="buttons is-centered has-addons">
                    <span v-for="m in availableMessengers" :key="m" class="button is-small" :class="{'is-warning': messenger==m}" @click="messenger=m">{{messengerNames[m]}}</span>
                </div>
            </div>
            <!-- Password input-->
            <div class="field">
                <div class="control has-icons-left has-icons-right">
                    <input v-model.trim="password"
                           v-validate="{required:true, min:4, max:20, regex:/^[^\s]+$/}"
                           data-vv-name="password"
                           data-vv-validate-on="input"
                           :placeholder="'components.common.quick-login-reg.registration.password' | translate"
                           class="input is-small"
                           :type="showPassword ? 'text' : 'password'"
                           maxlength="20"
                           ref="password" />
                    <span class="icon is-small is-left"><i class="fas fa-lock"></i></span>
                    <a class="icon is-small is-right is-clickable" @click="toggleShowPassword('password')">
                        <span v-show="!showPassword"><i class="far fa-eye-slash"></i></span>
                        <span v-show="showPassword"><i class="far fa-eye"></i></span>
                    </a>
                    <p class="help is-danger" v-if="errors.has('password:required')">{{"components.common.quick-login-reg.registration.validation.password required" | translate}}</p>
                    <p class="help is-danger" v-if="errors.has('password:min')">{{"components.common.quick-login-reg.registration.validation.password min length" | translate}}</p>
                    <p class="help is-danger" v-if="errors.has('password:max')">{{"components.common.quick-login-reg.registration.validation.password max length" | translate}}</p>
                </div>
            </div>
            <!-- Alias input-->
            <div v-if="askAliasDuringRegistration" class="field">
                <div class="control has-icons-left">
                    <input v-model.trim="alias"
                           v-validate="'min:3|max:50'"
                           data-vv-name="alias"
                           data-vv-validate-on="input"
                           :placeholder="'components.common.quick-login-reg.registration.alias' | translate"
                           type="text"
                           class="input is-small"
                           maxlength="30" />
                    <span class="icon is-small is-left"><i class="fas fa-user"></i></span>
                    <p class="help is-danger" v-if="errors.has('alias:min')">{{"components.common.quick-login-reg.registration.validation.alias min length" | translate}}</p>
                    <p class="help is-danger" v-if="errors.has('alias:max')">{{"components.common.quick-login-reg.registration.validation.alias max length" | translate}}</p>
                </div>
            </div>
            <!-- Recaptcha-->
            <recaptcha v-show="false" ref="recaptcha" :on-response="response => { recaptchaResponse = response; }" />
            <!-- Currency input-->
            <div v-if="showCurrencies" class="field center-field">
                <div class="control has-icons-left">
                    <div class="select is-small">
                        <select v-model="currency"
                                data-vv-name="currency"
                                data-vv-validate-on="change"
                                v-validate="'required'">
                            <option :value="null">
                                {{ 'components.common.quick-login-reg.registration.choose currency' | translate }}
                            </option>
                            <option v-for="(c, index) of visibleCurrencies" :key="index" :value="c.id">
                                {{ c.description }} ({{ c.id }})
                            </option>
                        </select>
                    </div>
                    <span class="icon is-left"><i class="fas fa-money-bill-alt"></i></span>
                    <p class="help is-danger" v-if="errors.has('currency:required')">{{"components.common.quick-login-reg.registration.validation.currency required" | translate}}</p>
                </div>
            </div>
            <!-- Agreement input-->
            <div class="field">
                <div class="control">
                    <label class="checkbox">
                        <input v-model="agreement"
                               v-validate="'required'"
                               data-vv-name="agreement"
                               data-vv-validate-on="change"
                               type="checkbox" />
                        <span class="is-size-7">{{'components.pages.registration.agreement' | translate}}</span>
                        <router-link :to="{ name: 'rules-inner', params: { rule: mainRulesRef && mainRulesRef.name } }" class="is-size-7">{{'components.pages.registration.agreement rules' | translate}}</router-link>
                    </label>
                    <p class="help is-danger" v-if="errors.has('agreement:required')">{{"components.pages.registration.validation.agreement required" | translate}}</p>
                </div>
            </div>

            <!-- Submit-->
            <button class="card-footer-item button is-warning is-small is-fullwidth" :class="{'is-loading': loading}">
                <span>{{'components.common.quick-login-reg.registration.continue' | translate}}</span>
                <span class="icon is-small"><i class="fas fa-arrow-right"></i></span>
            </button>
        </form>
    </div>
</template>

<script>
    import Vue from 'vue';

    import { mapGetters } from 'vuex';

    import registrationService from '../../../../../_common/services/registration.service';
    import currencyHelper from '../../../../../_common/helpers/currency.helper';
    import rulesHelper from '../../../../../_common/helpers/rules.helper';

    import Recaptcha from '../recaptcha/recaptcha.vue';
    import MaskedPhone from '../phone-number/phone-number-masked.vue';

    import UniqueLoginValidationRule from '../../../../../_common/mixins/unique-login.validation-rule.mixin';

    export default {
        name: 'quick-login-reg-registration-by-phone',
        components: { Recaptcha, MaskedPhone },
        mixins: [UniqueLoginValidationRule],
        $_veeValidate: { validator: 'new' },
        props: {
            goToStep2: {
                type: Function,
                required: true
            }
        },
        data() {
            return {
                showPassword: false,
                loading: false,
                phone: null,
                password: null,
                alias: null,
                messenger: null,
                error: null,
                recaptchaResponse: null,
                agreement: false,
                currency: null,
                mainRulesRef: null,
                visibleCurrencies: null,
                messengerNames: {
                    "sms": "SMS",
                    "wa": "WhatsApp",
                    "tg": "Telegram",
                    "vb": "Viber"
                }
            };
        },
        computed: {
            ...mapGetters(['client']),
            askAliasDuringRegistration() {
                return this.client && this.client.view.askAliasDuringRegistration;
            },
            showCurrencies() {
                return this.client && this.client.createDefaultBalance;
            },
            registrationBanner() {
                return this.client && this.client.view.registrationBanner;
            },
            availableMessengers() {
                return this.client && this.client.messengers.availableMessengers;
            },
            lang() {
                return Vue.state.lang.currentLang;
            }
        },
        async created() {
            this.messenger = this.availableMessengers[0];
            this.visibleCurrencies = await currencyHelper.getVisibleCurrencies();
            if (this.visibleCurrencies && this.visibleCurrencies.length) {
                this.currency = this.visibleCurrencies[0].id;
            }

            this.mainRulesRef = await rulesHelper.getMainRulesRef(this.lang);
        },
        methods: {
            toggleShowPassword(ref) {
                this.showPassword = !this.showPassword;
                setTimeout(() => this.$refs[ref].focus());
            },
            async submit() {
                await this.$validator.validateAll();
                if (this.errors.any() || this.loading) return;

                this.errors.clear();
                this.loading = true;
                try {
                    let response = await registrationService.registerByPhone({
                        phone: this.phone,
                        password: this.password,
                        alias: this.alias,
                        messenger: this.messenger,
                        language: Vue.state.lang.currentLang,
                        recaptchaResponse: this.recaptchaResponse,
                        currency: this.currency
                    });

                    if (response.sent) {
                        this.goToStep2({ phone: response.phone });
                    } else {
                        this.error = this.$t('components.common.quick-login-reg.registration.error ' + response.error.code);
                    }
                } catch (ex) {
                    console.log(ex);
                    let response = ex.response && ex.response.data;
                    this.error = this.$t('components.common.quick-login-reg.registration.error general');
                    if (response) {
                        console.error(`Failed to register new user: ${response}`);
                        if (response.code) {
                            this.error = this.$t('components.common.quick-login-reg.registration.error code ' + response.code);
                        }
                    }
                }
                this.loading = false;
            }
        }
    };
</script>

<style lang="scss">
</style>



// WEBPACK FOOTER //
// front/src/skins/bulmasite/components/common/quick-login-reg/registration-by-phone.vue